import { Modal, Spin, Table, Tabs } from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import IInvestorAccountIncomeTrust from '~Api/IncomeTrust/IInvestorAccountIncomeTrust';
import IInvestorIncomeTrustTransaction from '~Api/IncomeTrust/IInvestorIncomeTrustTransaction';
import classTypeLabels from '~Api/IncomeTrust/classTypeLabels';
import {
    investorAccountIncomeTrustTransactionsListAction,
} from '~Investors/actions';
import {
    investorAccountIncomeTrustTransactionsSelector,
} from '~Investors/selectors';
import { IGlobalState } from '~reducer';
import { IDictionary } from '~utilities/IDictionary';
import { currencyFormatter } from '~utilities/formatters';

interface ITransaction {
    amount: number;
    balance: number;
    description: string;
    transactionTime: string;
    uuid: string;
}

interface IProps {
    investorAccountIncomeTrust: IInvestorAccountIncomeTrust;
    isOpen: boolean;
    onCancel: () => void;
}

interface IPropsSelector {
    transactions: IDictionary<IInvestorIncomeTrustTransaction>;
}

interface IPropsDispatch {
    transactionsList: () => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class IncomeTrustTransactionsModal extends React.Component<Props> {
    public componentDidMount(): void {
        this.props.transactionsList();
    }

    public render(): JSX.Element {
        const { investorAccountIncomeTrust, isOpen, transactions } = this.props;

        if (!transactions) {
            return (
                <Modal
                    footer={false}
                    onCancel={this.props.onCancel}
                    open={isOpen}
                    title={`${classTypeLabels[investorAccountIncomeTrust.classType]} Income Trust Details`}
                    width={1000}
                >
                    <Spin/>
                </Modal>
            );
        }

        let interestBalance: number = 0;
        let principalBalance: number = 0;
        const interestTransactions: ITransaction[] = [];
        const principalTransactions: ITransaction[] = [];

        _.each(_.sortBy(transactions, ['transactionTime']), (transaction: IInvestorIncomeTrustTransaction) => {
            if (transaction.interestAmount !== 0) {
                interestBalance += transaction.interestAmount;

                interestTransactions.push({
                    amount: transaction.interestAmount,
                    balance: interestBalance,
                    description: transaction.description,
                    transactionTime: transaction.transactionTime,
                    uuid: transaction.uuid,
                });
            }

            if (transaction.principalAmount !== 0) {
                principalBalance += transaction.principalAmount;

                principalTransactions.push({
                    amount: transaction.principalAmount,
                    balance: principalBalance,
                    description: transaction.description,
                    transactionTime: transaction.transactionTime,
                    uuid: transaction.uuid,
                });
            }
        });

        const columns: ColumnType<ITransaction>[] = [
            {
                dataIndex: 'transactionTime',
                defaultSortOrder: 'descend',
                render: (transactionTime: string) => dayjs(transactionTime).format('D/M/YYYY HH:mm'),
                sorter: (a: ITransaction, b: ITransaction) => {
                    return a.transactionTime > b.transactionTime ? 1 : -1;
                },
                title: 'Date',
                width: '15%',
            },
            {
                dataIndex: 'description',
                title: 'Description',
            },
            {
                dataIndex: 'amount',
                render: (amount: number) => currencyFormatter.format(amount),
                title: 'Amount',
                width: '15%',
            },
            {
                dataIndex: 'balance',
                render: (balance: number) => currencyFormatter.format(balance),
                title: 'Balance',
                width: '15%',
            },
        ];

        return (
            <Modal
                footer={false}
                onCancel={this.props.onCancel}
                open={isOpen}
                title={`${classTypeLabels[investorAccountIncomeTrust.classType]} Income Trust Details`}
                width={1000}
            >
                <Tabs defaultActiveKey='principal'>
                    <Tabs.TabPane key='principal' tab='Principal'>
                        <Table
                            columns={columns}
                            dataSource={principalTransactions}
                            pagination={false}
                            rowKey='uuid'
                            size='middle'
                        />
                    </Tabs.TabPane>
                    <Tabs.TabPane key='interest' tab='Interest'>
                        <Table
                            columns={columns}
                            dataSource={interestTransactions}
                            pagination={false}
                            rowKey='uuid'
                            size='middle'
                        />
                    </Tabs.TabPane>
                </Tabs>
            </Modal>
        );
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        transactions: investorAccountIncomeTrustTransactionsSelector(state, ownProps.investorAccountIncomeTrust.uuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        transactionsList: () => dispatch(investorAccountIncomeTrustTransactionsListAction(ownProps.investorAccountIncomeTrust.uuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(IncomeTrustTransactionsModal);
